import React, { useRef, useState } from "react"
import ReactDOMServer from "react-dom/server"
import { AgGridReact } from "ag-grid-react"
import { Flight, Flights } from "../../api/Flight"
import { formatDateShort, formatDateShortwYear } from "../Utils"

import {
  ArrDepSorting,
  FlightTableTab,
  IUserSettingsSelection
} from "../../containers/selectionSlice"
import { FlightTableTopBar } from "./FlightTableTopBar"
import { ActionType, fieldOrder, PageGroup } from "../../common/transaction"
import { FragmentValues } from "../BreakoutPeriod"
import { FieldName, FlightText } from "../../common/text"
import {
  handleGetTransactionPageGroup,
  HandlingApp,
  PageId
} from "../../common/pageId"
import { AgGridReact as AgGridReactType } from "ag-grid-react/lib/agGridReact"
import { RowNode } from "ag-grid-community/dist/lib/entities/rowNode"
import { ValueGetterParams } from "ag-grid-community/dist/lib/entities/colDef"
import { ICellRendererParams } from "ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer"
import { SelectionChangedEvent } from "ag-grid-community/dist/lib/events"
import { ColDef } from "ag-grid-community/dist/lib/entities/colDef"
import { IExcludeFieldConfig } from "../../pages/Config"
import { Icon } from "@material-ui/core"
import { AirportSeasonConfigurations } from "../../api/AirportSeasonConfiguration"
import { ExportFormat } from "../DownloadPane"
import _ from "lodash"
import printDoc from "../../pdfExport/printDoc"
import { Column } from "ag-grid-community"
import { useAppSelector } from "../../app/hooks"
import useIsDesktopWidth from "../../hooks/useIsDesktopWidth"

export interface IMainTable {
  fragmentConfig?: IExcludeFieldConfig
  actionConfig?: ActionType[]
  onActionClick?: (action: ActionType) => void
  breakoutValues?: FragmentValues
  onChangeBreakout?: (values: FragmentValues) => void
  onRefresh: () => void
  pageId: PageId
  airportSeasons: AirportSeasonConfigurations
  onDownload?: (reportFormat: string) => void
  disableActionBtns?: boolean
  historyRowAirports?: string[]
}
interface Props extends IMainTable {
  flights: Flights
  selection?: boolean
  onSelect?: (flightIds: number[]) => void
  isViewPageType: boolean
}

export interface IPrintParams {
  PDF_HEADER_COLOR: string
  PDF_INNER_BORDER_COLOR: string
  PDF_OUTER_BORDER_COLOR: string
  PDF_PAGE_ORITENTATION: string
  PDF_WITH_FOOTER_PAGE_COUNT: boolean
  PDF_HEADER_HEIGHT: number
  PDF_ROW_HEIGHT: number
  PDF_ODD_BKG_COLOR: string
  PDF_EVEN_BKG_COLOR: string
  PDF_WITH_CELL_FORMATTING: boolean
}

export const FlightTable: React.FunctionComponent<Props> = ({
  flights,
  pageId,
  selection,
  breakoutValues,
  onChangeBreakout,
  airportSeasons,
  onSelect,
  onActionClick,
  fragmentConfig,
  onDownload,
  actionConfig,
  onRefresh,
  disableActionBtns,
  historyRowAirports,
  isViewPageType
}) => {
  const { season, useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const { reasonCodes, versionInfo } = useAppSelector(
    (state) => state["appReducer"]
  )
  const { useSSIMView }: IUserSettingsSelection = useAppSelector(
    (state) => state["selectionReducer"].userSettings
  )
  const pageGroup = handleGetTransactionPageGroup(pageId)
  const isSSIMView = pageGroup === PageGroup.HANDLING ? false : useSSIMView

  const [maximizeRowWidth, setMaximizeRowWidth] = useState<boolean>(false)
  const isGabaPages =
    pageId === PageId.ViewFlightsGABA || pageId === PageId.EditFlightsGABA
  const isHandlingPages =
    pageId === PageId.ViewHandling || pageId === PageId.EditHandling
  const isPPR =
    isHandlingPages && versionInfo.handlingApplication === HandlingApp.FRAPORTGR
  const editPages = [
    PageId.EditFlightsSeries,
    PageId.EditFlightsSD,
    PageId.EditSHL,
    PageId.EditOffers
  ]

  const isEditPage = editPages.includes(pageId)

  const isSingleEdit = pageId === PageId.EditFlightsSD
  const isViewFlightsEUACA = pageId === PageId.ViewFlightsEUACA
  const isEditFlightsSHL = pageId === PageId.EditSHL
  const isEditFlightsSeries = pageId === PageId.EditFlightsSeries

  const { chosenFlightTableTab, chosenArrDepSorting } = useAppSelector(
    (state) => state["selectionReducer"].flightTableSelection
  )
  const grid = useRef<AgGridReactType>(null)

  const handleGetTooltip = (value: string) => {
    if (value === "9999") {
      return "No slot allocated"
    }

    const reasonCode = reasonCodes.find((rc) => rc.code === value)
    return reasonCode ? reasonCode.description : ""
  }
  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)
  const [disabledControls, setDisabledControls] = useState([])

  const handleGetPairCell = (firstVal: string, secondVal: string) => {
    const noFirstVal = firstVal === ""
    const noSecondVal = secondVal === ""
    const firstValText = noFirstVal ? secondVal : firstVal
    const secondValText = noSecondVal ? firstVal : secondVal

    return ReactDOMServer.renderToStaticMarkup(
      <span className="d-flex align-items-center h-100p">
        <span
          style={{
            width: "50%",
            visibility: noFirstVal ? "hidden" : "visible"
          }}
          title={handleGetTooltip(firstValText)}
          className={firstVal === "9999" ? "no-slot-val" : ""}
        >
          {firstValText}
        </span>
        <span style={{ width: "4px" }} />
        <span
          style={{
            width: "50%",
            visibility: noSecondVal ? "hidden" : "visible"
          }}
          title={handleGetTooltip(secondValText)}
          className={secondVal === "9999" ? "no-slot-val" : ""}
        >
          {secondValText}
        </span>
      </span>
    )
  }

  const handleGetDateCell = (p: ICellRendererParams) => {
    const startDateVal = formatDateShort(new Date(p.data.startDate))
    const endDateVal = formatDateShort(new Date(p.data.endDate))
    const dateVal =
      !isGabaPages && !isSingleEdit && !isHandlingPages
        ? startDateVal + " " + endDateVal
        : startDateVal
    return ReactDOMServer.renderToStaticMarkup(
      <span className="d-flex align-items-center">
        <span>{dateVal}</span>
        {p.data.startsOutsideSelection && (
          <Icon
            title="Flight starts before selected date"
            className="cell-info"
          >
            info_outline
          </Icon>
        )}
      </span>
    )
  }

  const handleGetReqDateCell = (p: ICellRendererParams) => {
    const startReqDateVal = p.data.requiredDateArr
      ? formatDateShort(new Date(p.data.requiredDateArr))
      : ""
    const endReqDateVal = p.data.requiredDateDep
      ? formatDateShort(new Date(p.data.requiredDateDep))
      : ""
    const dateVal = isHandlingPages
      ? startReqDateVal + " " + endReqDateVal
      : startReqDateVal

    return ReactDOMServer.renderToStaticMarkup(
      <span className="d-flex align-items-center">
        <span>{dateVal}</span>
      </span>
    )
  }

  const handleCompareRows = (
    aData: Flight,
    bData: Flight,
    arrFieldName: FieldName,
    depFieldName: FieldName
  ) => {
    let valueA = aData[arrFieldName] ? aData[arrFieldName] : aData[depFieldName]
    let valueB = bData[arrFieldName] ? bData[arrFieldName] : bData[depFieldName]

    if (chosenArrDepSorting === ArrDepSorting.Dep) {
      valueA = aData[depFieldName]
      valueB = bData[depFieldName]
    }

    if (chosenArrDepSorting === ArrDepSorting.Arr) {
      valueA = aData[arrFieldName]
      valueB = bData[arrFieldName]
    }

    return valueA === valueB ? 0 : valueA > valueB ? 1 : -1
  }

  const handleCompareDates = (startDateA: string, startDateB: string) => {
    const a = new Date(startDateA).getTime()
    const b = new Date(startDateB).getTime()
    return a === b ? 0 : a > b ? 1 : -1
  }

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedNodes = event.api.getSelectedNodes()

    // Get selected transactions airports
    const selectedRowData = event.api.getSelectedRows()
    const selectedAirports = selectedRowData.map(
      (selectedRow) => selectedRow.airport
    )

    const uniqueSelectedAirports = Array.from(new Set(selectedAirports))
    // Get array with selected airport Seasons
    const selectedAirportSeasons = airportSeasons.filter((person) =>
      uniqueSelectedAirports.includes(person.airport)
    )

    const disabledControlsArr = selectedAirportSeasons.map(
      (a) => a.disabledControls
    )
    const flattenArr = (arr: any[]) =>
      arr.reduce(
        (acc: string | any[], val: any) =>
          Array.isArray(val) ? acc.concat(flattenArr(val)) : acc.concat(val),
        []
      )

    const flattenDisabledControlsArr = flattenArr(disabledControlsArr)

    setDisabledControls(Array.from(new Set(flattenDisabledControlsArr)))

    handleUpdateSelectedFlightIds(selectedNodes)
  }

  const handleUpdateSelectedFlightIds = (selectedNodes: RowNode[]) => {
    const sortedSelectedNodes = _.cloneDeep(selectedNodes).sort(
      (a, b) => a.rowIndex - b.rowIndex
    )

    const selectedFlightIds = sortedSelectedNodes.map(
      (r: RowNode) => r.data.flightId
    )
    onSelect(selectedFlightIds)
  }

  const handleCheckColumnWidth = (gridWidth: number, columns: Column[]) => {
    let headerWidth = 0
    columns.forEach((c) => {
      const columnWidth = c.getActualWidth()
      headerWidth = headerWidth + columnWidth
    })

    setMaximizeRowWidth(gridWidth > headerWidth ? true : false)
  }

  const handleGetColumnKeys = () => {
    let keys = [
      FieldName.airport,
      FieldName.responseCode,
      FieldName.flightNoArr,
      FieldName.flightNoDep,
      FieldName.serviceTypeArr,
      FieldName.serviceTypeDep,
      FieldName.clearedTimeArr,
      FieldName.clearedTimeDep,
      FieldName.requiredTimeArr,
      FieldName.requiredTimeDep,
      FieldName.turnaroundDays,
      FieldName.minGroundTime,
      FieldName.aircraftType,
      FieldName.numSeats,
      FieldName.aircraftRegistration,
      FieldName.routeArr,
      FieldName.routeDep,
      FieldName.terminalArr,
      FieldName.terminalDep,
      FieldName.slotIdArr,
      FieldName.slotIdDep
    ]

    if (!isGabaPages || !isHandlingPages) {
      //TODO: test this
      let t = [
        FieldName.period,
        FieldName.doop,
        FieldName.woop,
        FieldName.noOfOperations
      ]
      keys = keys.concat(t)
    } else {
      keys.push(FieldName.startDate)
    }

    if (!isPPR) {
      keys = keys.concat([FieldName.reasonCodeArr, FieldName.reasonCodeDep])
    }

    keys.sort((a, b) => {
      const aVal = fieldOrder.find((f) => f.type === a)
      const bVal = fieldOrder.find((f) => f.type === b)
      const orderFormat = isSSIMView ? "SSIMOrder" : "categoryOrder"
      return (
        ((isGabaPages || isHandlingPages) && aVal.gabaOrder
          ? aVal.gabaOrder
          : aVal[orderFormat]) -
        ((isGabaPages || isHandlingPages) && bVal.gabaOrder
          ? bVal.gabaOrder
          : bVal[orderFormat])
      )
    })

    return keys
  }
  const printParams = {
    PDF_HEADER_COLOR: "#069ddf",
    PDF_INNER_BORDER_COLOR: "#fff",
    PDF_OUTER_BORDER_COLOR: "#fff",
    PDF_PAGE_ORITENTATION: "landscape",
    PDF_WITH_FOOTER_PAGE_COUNT: true,
    PDF_HEADER_HEIGHT: 25,
    PDF_ROW_HEIGHT: 15,
    PDF_ODD_BKG_COLOR: "#cae8f6",
    PDF_EVEN_BKG_COLOR: "#fff",
    PDF_WITH_CELL_FORMATTING: true
  }
  const title = pageId + "_" + season + "_" + formatDateShortwYear(new Date())
  const handleExport = (format: string) => {
    if (format === ExportFormat.PDF) {
      printDoc(printParams, gridApi, columnApi, title)
    }

    if (format === ExportFormat.CSVSemicolon || format === ExportFormat.CSV) {
      grid.current?.api.exportDataAsCsv({
        fileName: title,
        processCellCallback: (p) => {
          let cellValue = p.value
          const colId = p.column.getColId()

          if (colId === "period") {
            cellValue =
              formatDateShort(new Date(p.node.data.startDate)) +
              " " +
              formatDateShort(new Date(p.node.data.endDate))
          }
          return cellValue
        },
        columnKeys: handleGetColumnKeys(),
        columnSeparator: format === ExportFormat.CSVSemicolon ? ";" : ","
      })
    }
  }

  let columns: ColDef[] = [
    {
      headerName: FlightText.ap,
      field: FieldName.airport,
      autoHeight: true,
      checkboxSelection: selection ? true : false,
      headerCheckboxSelection: selection ? true : false
    },
    {
      headerName: FlightText.responseCode,
      field: FieldName.responseCode,
      autoHeight: true,
      hide: isViewFlightsEUACA
    },
    {
      headerName: FlightText.arrFlt,
      field: FieldName.flightNoArr,

      valueGetter: (p: ValueGetterParams) =>
        p.data.operatorArr + " " + p.data.serviceNoArr
    },
    {
      headerName: FlightText.depFlt,
      field: FieldName.flightNoDep,
      valueGetter: (p: ValueGetterParams) =>
        p.data.operatorDep + " " + p.data.serviceNoDep
    },
    {
      headerName: FlightText.st,
      field: FieldName.serviceTypes,
      cellRenderer: (p: ICellRendererParams) => {
        return handleGetPairCell(p.data.serviceTypeArr, p.data.serviceTypeDep)
      },
      valueGetter: (p: ValueGetterParams) =>
        p.data.serviceTypeArr + p.data.serviceTypeDep,
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: RowNode,
        nodeB: RowNode
      ) => {
        return handleCompareRows(
          nodeA.data,
          nodeB.data,
          FieldName.serviceTypeArr,
          FieldName.serviceTypeDep
        )
      }
    },
    {
      headerName: "ASTC",
      field: FieldName.serviceTypeArr,
      hide: true
    },
    {
      headerName: "DSTC",
      field: FieldName.serviceTypeDep,
      hide: true
    },
    {
      headerName:
        !isGabaPages && !isSingleEdit && !isHandlingPages
          ? FlightText.dateRange
          : FlightText.date,
      field: FieldName.period,
      cellRenderer: handleGetDateCell,
      valueGetter: (p: ValueGetterParams) =>
        formatDateShort(new Date(p.data.startDate)) +
        " " +
        formatDateShort(new Date(p.data.endDate)),
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: RowNode,
        nodeB: RowNode
      ) => {
        return handleCompareDates(nodeA.data.startDate, nodeB.data.startDate)
      }
    },
    {
      headerName: "Date",
      field: FieldName.startDate,
      valueGetter: (p: ValueGetterParams) =>
        formatDateShort(new Date(p.data.startDate)),
      hide: true
    },

    {
      headerName: FlightText.reqDate,
      field: FieldName.reqDate,
      cellRenderer: handleGetReqDateCell,
      valueGetter: (p: ValueGetterParams) =>
        p.data.requiredDateArr + " " + p.data.requiredDateDep,
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: RowNode,
        nodeB: RowNode
      ) => {
        return handleCompareDates(
          nodeA.data.requiredDateArr,
          nodeB.data.requiredDateDep
        )
      },
      hide: !isHandlingPages
    },

    {
      headerName: FlightText.doop,
      field: FieldName.doop,
      hide: isGabaPages || isSingleEdit || isHandlingPages
    },
    {
      headerName: FlightText.f,
      field: FieldName.woop,
      hide: isGabaPages || isSingleEdit || isHandlingPages
    },
    {
      headerName: FlightText.clearTimeTwo,
      field: FieldName.clearedTimes,
      cellRenderer: (p: ICellRendererParams) => {
        return handleGetPairCell(p.data.clearedTimeArr, p.data.clearedTimeDep)
      },
      valueGetter: (p: ValueGetterParams) =>
        p.data.clearedTimeArr + "  " + p.data.clearedTimeDep,
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: RowNode,
        nodeB: RowNode
      ) => {
        return handleCompareRows(
          nodeA.data,
          nodeB.data,
          FieldName.clearedTimeArr,
          FieldName.clearedTimeDep
        )
      }
    },
    {
      headerName: "ATime",
      field: FieldName.clearedTimeArr,

      hide: true
    },
    {
      headerName: "DTime",
      field: FieldName.clearedTimeDep,
      hide: true
    },
    {
      headerName: FlightText.reqTime,
      field: FieldName.requiredTimes,
      cellRenderer: (p: ICellRendererParams) => {
        return handleGetPairCell(p.data.requiredTimeArr, p.data.requiredTimeDep)
      },
      valueGetter: (p: ValueGetterParams) =>
        p.data.requiredTimeArr + "  " + p.data.requiredTimeDep,
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: RowNode,
        nodeB: RowNode
      ) => {
        return handleCompareRows(
          nodeA.data,
          nodeB.data,
          FieldName.requiredTimeArr,
          FieldName.requiredTimeDep
        )
      }
    },
    {
      headerName: "AReq",
      field: FieldName.requiredTimeArr,
      hide: true
    },
    {
      headerName: "DReq",
      field: FieldName.requiredTimeDep,
      hide: true
    },
    {
      headerName: FlightText.reason,
      field: FieldName.reasonCodes,
      cellRenderer: (p: ICellRendererParams) => {
        return handleGetPairCell(p.data.reasonCodeArr, p.data.reasonCodeDep)
      },
      valueGetter: (p: ValueGetterParams) =>
        p.data.reasonCodeArr + "  " + p.data.reasonCodeDep,
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: RowNode,
        nodeB: RowNode
      ) => {
        return handleCompareRows(
          nodeA.data,
          nodeB.data,
          FieldName.reasonCodeArr,
          FieldName.reasonCodeDep
        )
      },
      hide: isPPR
    },
    {
      headerName: "AReason",
      field: FieldName.reasonCodeArr,
      hide: true
    },
    {
      headerName: "DReason",
      field: FieldName.reasonCodeDep,
      hide: true
    },
    {
      headerName: FlightText.o,
      field: FieldName.turnaroundDays
    },
    {
      headerName: FlightText.mgt,
      field: FieldName.minGroundTime,
      hide: isViewFlightsEUACA
    },
    {
      headerName: FlightText.aft,
      field: FieldName.aircraftType
    },
    {
      headerName: FlightText.seats,
      field: FieldName.numSeats
    },
    {
      headerName: FlightText.acReg,
      field: FieldName.aircraftRegistration,
      hide: isEditFlightsSeries || isEditFlightsSHL
    },
    {
      headerName:
        !isGabaPages && !isHandlingPages
          ? FlightText.aRoute
          : FlightText.origin,
      field: FieldName.routeArr,
      valueGetter: (p: ValueGetterParams) =>
        !isGabaPages && !isHandlingPages
          ? p.data.originStation + " " + p.data.lastStation
          : p.data.originStation
    },

    {
      headerName:
        !isGabaPages && !isHandlingPages ? FlightText.dRoute : FlightText.dest,
      field: FieldName.routeDep,
      valueGetter: (p: ValueGetterParams) =>
        !isGabaPages && !isHandlingPages
          ? p.data.nextStation + " " + p.data.destinationStation
          : p.data.destinationStation
    },

    {
      headerName: FlightText.terminal,
      field: FieldName.terminals,
      hide: isViewFlightsEUACA,
      cellRenderer: (p: ICellRendererParams) => {
        return handleGetPairCell(p.data.terminalArr, p.data.terminalDep)
      },
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: RowNode,
        nodeB: RowNode
      ) => {
        return handleCompareRows(
          nodeA.data,
          nodeB.data,
          FieldName.terminalArr,
          FieldName.terminalDep
        )
      }
    },
    {
      headerName: "ATerminal",
      field: FieldName.terminalArr,
      hide: true
    },
    {
      headerName: "DTerminal",
      field: FieldName.terminalDep,
      hide: true
    },

    {
      headerName: isPPR ? FlightText.pprId : FlightText.slotId,
      field: FieldName.slotId,
      hide: isEditPage,
      cellRenderer: (p: ICellRendererParams) => {
        return isPPR
          ? p.data.slotIdArr
          : handleGetPairCell(p.data.slotIdArr, p.data.slotIdDep)
      },
      valueGetter: (p: ValueGetterParams) =>
        isPPR ? p.data.slotIdArr : p.data.slotIdArr + " " + p.data.slotIdDep,
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: RowNode,
        nodeB: RowNode
      ) => {
        return handleCompareRows(
          nodeA.data,
          nodeB.data,
          FieldName.slotIdArr,
          FieldName.slotIdDep
        )
      }
    },
    {
      headerName: isPPR ? FlightText.aPPRId : FlightText.aSlotId,
      field: FieldName.slotIdArr,
      hide: true
    },
    {
      headerName: isPPR ? FlightText.dPPRId : FlightText.ddSlotId,
      field: FieldName.slotIdDep,
      hide: true
    },
    {
      headerName: FlightText.ops,
      field: FieldName.noOfOperations,
      hide: isGabaPages || isSingleEdit || isViewFlightsEUACA || isHandlingPages
    },
    {
      headerName: FlightText.handler,
      field: FieldName.handler,
      hide: !isHandlingPages
    },
    {
      headerName: FlightText.parkLoc,
      field: FieldName.parkingLocation,
      hide: !isGabaPages
    },
    {
      headerName: FlightText.fr,
      field: FieldName.vfr,
      hide: !isHandlingPages,
      valueGetter: (p: ValueGetterParams) => (p.data.vfr ? "VFR" : "IFR")
    },
    {
      headerName: "TB",
      field: FieldName.towbar,
      hide: !isHandlingPages,
      valueGetter: (p: ValueGetterParams) => (p.data.towbar ? "Yes" : "No")
    },
    {
      headerName: FlightText.mtow,
      field: FieldName.mtow,
      hide: !isHandlingPages
    },
    {
      headerName: FlightText.gl,
      field: FieldName.gabaLounge,
      hide: !isHandlingPages,
      valueGetter: (p: ValueGetterParams) => (p.data.gabaLounge ? "Yes" : "No")
    },
    {
      headerName: FlightText.rf,
      field: FieldName.refuelling,
      hide: !isHandlingPages,
      valueGetter: (p: ValueGetterParams) => (p.data.refuelling ? "Yes" : "No")
    },
    {
      headerName: "OT",
      field: FieldName.operatorText,
      hide: !isHandlingPages
    }
    // {
    //   headerName: FlightText.noise,
    //   field: FieldName.noiseCertificate,
    //   hide: !isHandlingPages
    // }
  ]

  const orderedColumns = _.cloneDeep(columns).sort((a, b) => {
    const aVal = fieldOrder.find((f) => f.type === a.field)
    const bVal = fieldOrder.find((f) => f.type === b.field)
    const orderFormat = isSSIMView ? "SSIMOrder" : "categoryOrder"
    const useGabaOrder = isGabaPages || isSingleEdit || isHandlingPages

    return (
      (useGabaOrder && aVal.gabaOrder ? aVal.gabaOrder : aVal[orderFormat]) -
      (useGabaOrder && bVal.gabaOrder ? bVal.gabaOrder : bVal[orderFormat])
    )
  })

  const isDesktopWidth = useIsDesktopWidth()
  return (
    <div
      className={
        "ocs-view-flights-table-container" +
        (useLocalTime ? " ocs-local-time-table" : "") +
        (chosenFlightTableTab === FlightTableTab.condensed
          ? " ocs-condensed-table"
          : "") +
        (chosenFlightTableTab === FlightTableTab.spacious
          ? " ocs-spacious-table"
          : "")
      }
    >
      <FlightTableTopBar
        pageId={pageId}
        airportSeasons={airportSeasons}
        onActionClick={onActionClick}
        breakoutValues={breakoutValues}
        onChangeBreakout={onChangeBreakout}
        fragmentConfig={fragmentConfig}
        actionConfig={actionConfig}
        onRefresh={onRefresh}
        onExport={handleExport}
        onDownload={onDownload}
        historyRowAirports={historyRowAirports}
        disableActionBtns={disableActionBtns}
        isViewPageType={isViewPageType}
        disabledControls={disabledControls}
        isHandlingPages={isHandlingPages}
      />

      <div
        className={
          "ocs-table-grid ag-theme-alpine" +
          (maximizeRowWidth ? " max-row-width" : "")
        }
      >
        <AgGridReact
          ref={grid}
          columnDefs={orderedColumns}
          rowData={flights}
          defaultColDef={{ sortable: true }}
          onGridReady={(params) => {
            setGridApi(params.api)
            setColumnApi(params.columnApi)
            if (isDesktopWidth) {
              params.api.sizeColumnsToFit()
            }
          }}
          onFirstDataRendered={(params) => {
            params.columnApi.autoSizeAllColumns()
          }}
          onComponentStateChanged={(params) => {
            params.columnApi.autoSizeAllColumns()
          }}
          onGridSizeChanged={(params) => {
            handleCheckColumnWidth(
              params.clientWidth,
              params.columnApi.getAllDisplayedColumns()
            )
          }}
          onSortChanged={(params) => {
            // updating sort order of selected flight ids
            const selectedNodes = params.api.getSelectedNodes()
            handleUpdateSelectedFlightIds(selectedNodes)
          }}
          rowSelection="multiple"
          suppressRowClickSelection
          suppressRowHoverHighlight
          onSelectionChanged={handleSelectionChanged}
          isRowSelectable={(node) =>
            node.data
              ? node.data.selectionStatus === "1" ||
                node.data.selectionStatus === "2"
                ? false
                : true
              : false
          }
          getRowClass={(p) =>
            p.data
              ? p.data.selectionStatus === "0"
                ? "fragmented"
                : p.data.selectionStatus === "1"
                ? "using-on-changescreen"
                : undefined
              : undefined
          }
        />
      </div>
    </div>
  )
}
